import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Plus,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Eye,
  Pencil,
  Trash2,
  Factory,
  CalendarDays,
  X,
  Settings,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Helpers ───────────────────────────────────────────
function formatDate(d) {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

// ── Dynamic Field Renderer ────────────────────────────
function DynamicField({ field, value, onChange }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground mb-1 block">{field.label}</Label>
      <Input
        type={field.type === "number" ? "number" : "text"}
        step={field.type === "number" ? "any" : undefined}
        value={value ?? ""}
        onChange={(e) => onChange(field.key, field.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
        placeholder={field.label}
        className="h-9"
        data-testid={`field-${field.key}`}
      />
    </div>
  );
}

// ── View Detail Dialog ────────────────────────────────
function ViewLogDialog({ log, process, open, onClose }) {
  if (!log || !process) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Factory className="h-5 w-5" />
            {log.process_name} - {log.order_number}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          {/* Order Info */}
          <div className="grid grid-cols-2 gap-3 p-3 bg-muted/50 rounded-md">
            <div><span className="text-muted-foreground">Order:</span> <span className="font-medium">{log.order_number}</span></div>
            <div><span className="text-muted-foreground">Item:</span> <span className="font-medium">{log.item_name}</span></div>
            <div><span className="text-muted-foreground">Customer:</span> <span className="font-medium">{log.customer_name}</span></div>
            <div><span className="text-muted-foreground">Batch:</span> <span className="font-medium">{log.batch_no || "-"}</span></div>
          </div>
          {/* Production Info */}
          <div className="grid grid-cols-2 gap-3 p-3 bg-muted/50 rounded-md">
            <div><span className="text-muted-foreground">Machine:</span> <span className="font-medium">{log.machine_name || "-"}</span></div>
            <div><span className="text-muted-foreground">Operator:</span> <span className="font-medium">{log.operator_name || "-"}</span></div>
            <div><span className="text-muted-foreground">People:</span> <span className="font-medium">{log.total_people ?? "-"}</span></div>
            <div><span className="text-muted-foreground">Date:</span> <span className="font-medium">{formatDate(log.production_date)}</span></div>
            <div><span className="text-muted-foreground">Start:</span> <span className="font-medium">{log.start_time || "-"}</span></div>
            <div><span className="text-muted-foreground">Stop:</span> <span className="font-medium">{log.stop_time || "-"}</span></div>
            <div><span className="text-muted-foreground">Setup Time:</span> <span className="font-medium">{log.setup_time_minutes ? `${log.setup_time_minutes} min` : "-"}</span></div>
          </div>
          {/* Quantities */}
          <div className="grid grid-cols-3 gap-3 p-3 bg-muted/50 rounded-md">
            <div><span className="text-muted-foreground">Planned:</span> <span className="font-medium">{log.planned_qty ?? "-"} {process.planned_qty_unit}</span></div>
            <div><span className="text-muted-foreground">Input:</span> <span className="font-medium">{log.input_qty ?? "-"} {process.input_qty_unit}</span></div>
            <div><span className="text-muted-foreground">Output:</span> <span className="font-medium">{log.output_qty ?? "-"} {process.output_qty_unit}</span></div>
          </div>
          {/* Wastage */}
          {log.wastage && Object.keys(log.wastage).length > 0 && (
            <div className="p-3 bg-muted/50 rounded-md">
              <span className="text-muted-foreground font-medium">Wastage: </span>
              {Object.entries(log.wastage).map(([unit, val]) => (
                <Badge key={unit} variant="outline" className="mr-2">{val} {unit}</Badge>
              ))}
            </div>
          )}
          {/* Avg Production */}
          {log.avg_production_per_hr != null && (
            <div className="p-3 bg-muted/50 rounded-md">
              <span className="text-muted-foreground">Avg Production/Hr:</span>{" "}
              <span className="font-medium">{log.avg_production_per_hr} {process.avg_production_unit}</span>
            </div>
          )}
          {/* Process Specific Fields */}
          {log.specific_data && Object.keys(log.specific_data).length > 0 && (
            <div className="p-3 bg-muted/50 rounded-md space-y-1">
              <p className="font-medium text-muted-foreground mb-2">Process Details</p>
              <div className="grid grid-cols-2 gap-2">
                {process.specific_fields?.map((f) => {
                  const val = log.specific_data[f.key];
                  if (val == null || val === "") return null;
                  return (
                    <div key={f.key}>
                      <span className="text-muted-foreground">{f.label}:</span>{" "}
                      <span className="font-medium">{val}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* Output Item */}
          {log.output_item && (
            <div className="p-3 bg-muted/50 rounded-md">
              <span className="text-muted-foreground">Output Item:</span>{" "}
              <span className="font-medium">{log.output_item}</span>
            </div>
          )}
          {/* Custom Fields */}
          {log.custom_field_data && Object.keys(log.custom_field_data).length > 0 && (
            <div className="p-3 bg-muted/50 rounded-md space-y-1">
              <p className="font-medium text-muted-foreground mb-2 flex items-center gap-2">Custom Fields <Badge variant="secondary" className="text-xs">Custom</Badge></p>
              <div className="grid grid-cols-2 gap-2">
                {process.custom_fields?.map((f) => {
                  const val = log.custom_field_data[f.key];
                  if (val == null || val === "") return null;
                  return (
                    <div key={f.key}>
                      <span className="text-muted-foreground">{f.label}:</span>{" "}
                      <span className="font-medium">{val}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* Remarks */}
          {log.remarks && (
            <div className="p-3 bg-muted/50 rounded-md">
              <span className="text-muted-foreground">Remarks:</span>{" "}
              <span className="font-medium">{log.remarks}</span>
            </div>
          )}
          <div className="text-xs text-muted-foreground pt-2">
            Created by {log.created_by_name} on {formatDate(log.created_at)}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Create / Edit Dialog ──────────────────────────────
function LogFormDialog({ open, onClose, onSave, processes, editLog }) {
  const [orders, setOrders] = useState([]);
  const [orderSearch, setOrderSearch] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [selectedProcessId, setSelectedProcessId] = useState("");
  const [formData, setFormData] = useState({});
  const [specificData, setSpecificData] = useState({});
  const [customFieldData, setCustomFieldData] = useState({});
  const [wastage, setWastage] = useState({});
  const [saving, setSaving] = useState(false);

  const selectedProcess = processes.find((p) => p.process_id === selectedProcessId);

  // Load orders
  useEffect(() => {
    if (!open) return;
    const fetchOrders = async () => {
      try {
        const res = await axios.get(`${API}/api/production/orders-for-log`, { params: { search: orderSearch || undefined } });
        setOrders(res.data);
      } catch { /* ignore */ }
    };
    const timer = setTimeout(fetchOrders, 300);
    return () => clearTimeout(timer);
  }, [open, orderSearch]);

  // Populate edit data
  useEffect(() => {
    if (!open) {
      setSelectedOrder(null);
      setSelectedItemId("");
      setSelectedProcessId("");
      setFormData({});
      setSpecificData({});
      setCustomFieldData({});
      setWastage({});
      return;
    }
    if (editLog) {
      setSelectedProcessId(editLog.process_id);
      setSelectedItemId(editLog.item_id);
      setFormData({
        batch_no: editLog.batch_no || "",
        machine_name: editLog.machine_name || "",
        operator_name: editLog.operator_name || "",
        total_people: editLog.total_people ?? "",
        production_date: editLog.production_date || "",
        start_time: editLog.start_time || "",
        stop_time: editLog.stop_time || "",
        setup_time_minutes: editLog.setup_time_minutes ?? "",
        planned_qty: editLog.planned_qty ?? "",
        input_qty: editLog.input_qty ?? "",
        output_qty: editLog.output_qty ?? "",
        output_item: editLog.output_item || "",
        avg_production_per_hr: editLog.avg_production_per_hr ?? "",
        remarks: editLog.remarks || "",
      });
      setSpecificData(editLog.specific_data || {});
      setCustomFieldData(editLog.custom_field_data || {});
      setWastage(editLog.wastage || {});
      // Mock order for display
      setSelectedOrder({
        order_id: editLog.order_id,
        order_number: editLog.order_number,
        customer_name: editLog.customer_name,
        items: [{ item_id: editLog.item_id, item_name: editLog.item_name }],
      });
    }
  }, [open, editLog]);

  const handleFieldChange = (key, val) => setFormData((p) => ({ ...p, [key]: val }));
  const handleSpecificChange = (key, val) => setSpecificData((p) => ({ ...p, [key]: val }));
  const handleCustomFieldChange = (key, val) => setCustomFieldData((p) => ({ ...p, [key]: val }));
  const handleWastageChange = (unit, val) => setWastage((p) => ({ ...p, [unit]: val === "" ? "" : Number(val) }));

  const handleSubmit = async () => {
    if (!selectedOrder || !selectedItemId || !selectedProcessId) return;
    setSaving(true);
    try {
      const payload = {
        process_id: selectedProcessId,
        order_id: selectedOrder.order_id,
        item_id: selectedItemId,
        ...formData,
        total_people: formData.total_people === "" ? null : Number(formData.total_people),
        setup_time_minutes: formData.setup_time_minutes === "" ? null : Number(formData.setup_time_minutes),
        planned_qty: formData.planned_qty === "" ? null : Number(formData.planned_qty),
        input_qty: formData.input_qty === "" ? null : Number(formData.input_qty),
        output_qty: formData.output_qty === "" ? null : Number(formData.output_qty),
        avg_production_per_hr: formData.avg_production_per_hr === "" ? null : Number(formData.avg_production_per_hr),
        specific_data: specificData,
        custom_field_data: customFieldData,
        wastage: Object.fromEntries(Object.entries(wastage).filter(([, v]) => v !== "" && v != null)),
      };
      if (editLog) {
        await axios.put(`${API}/api/production/logs/${editLog.log_id}`, payload);
      } else {
        await axios.post(`${API}/api/production/logs`, payload);
      }
      onSave();
      onClose();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editLog ? "Edit Production Log" : "New Production Log Entry"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* ── Section 1: Order & Item ── */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Order & Item</h3>
            {!editLog && (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search orders by number or customer..."
                    value={orderSearch}
                    onChange={(e) => setOrderSearch(e.target.value)}
                    className="pl-9 h-9"
                    data-testid="order-search-input"
                  />
                </div>
                {orders.length > 0 && !selectedOrder && (
                  <div className="border rounded-md max-h-40 overflow-y-auto">
                    {orders.slice(0, 20).map((o) => (
                      <div
                        key={o.order_id}
                        className="px-3 py-2 hover:bg-muted cursor-pointer text-sm flex justify-between"
                        onClick={() => { setSelectedOrder(o); setSelectedItemId(""); }}
                        data-testid={`order-option-${o.order_id}`}
                      >
                        <span className="font-medium">{o.order_number}</span>
                        <span className="text-muted-foreground">{o.customer_name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            {selectedOrder && (
              <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-md text-sm">
                <Badge variant="secondary">{selectedOrder.order_number}</Badge>
                <span>{selectedOrder.customer_name}</span>
                {!editLog && (
                  <Button variant="ghost" size="icon" className="h-6 w-6 ml-auto" onClick={() => { setSelectedOrder(null); setSelectedItemId(""); }}>
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            )}
            {selectedOrder && (
              <Select value={selectedItemId} onValueChange={setSelectedItemId} disabled={!!editLog}>
                <SelectTrigger className="h-9" data-testid="item-select">
                  <SelectValue placeholder="Select Item" />
                </SelectTrigger>
                <SelectContent>
                  {selectedOrder.items?.map((it) => (
                    <SelectItem key={it.item_id} value={it.item_id}>{it.item_name || it.item_id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <Separator />

          {/* ── Section 2: Process ── */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Production Process</h3>
            <Select value={selectedProcessId} onValueChange={setSelectedProcessId} disabled={!!editLog}>
              <SelectTrigger className="h-9" data-testid="process-select">
                <SelectValue placeholder="Select Process" />
              </SelectTrigger>
              <SelectContent>
                {processes.map((p) => (
                  <SelectItem key={p.process_id} value={p.process_id}>{p.process_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedProcess && (
            <>
              <Separator />
              {/* ── Section 3: Common Fields ── */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground">Production Details</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Batch No</Label>
                    <Input value={formData.batch_no || ""} onChange={(e) => handleFieldChange("batch_no", e.target.value)} className="h-9" data-testid="field-batch-no" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">{selectedProcess.machine_label}</Label>
                    <Input value={formData.machine_name || ""} onChange={(e) => handleFieldChange("machine_name", e.target.value)} className="h-9" data-testid="field-machine-name" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Operator Name</Label>
                    <Input value={formData.operator_name || ""} onChange={(e) => handleFieldChange("operator_name", e.target.value)} className="h-9" data-testid="field-operator-name" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Total No Of People</Label>
                    <Input type="number" value={formData.total_people ?? ""} onChange={(e) => handleFieldChange("total_people", e.target.value)} className="h-9" data-testid="field-total-people" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">{selectedProcess.date_label}</Label>
                    <Input type="date" value={formData.production_date || ""} onChange={(e) => handleFieldChange("production_date", e.target.value)} className="h-9" data-testid="field-production-date" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Setup Time (minutes)</Label>
                    <Input type="number" value={formData.setup_time_minutes ?? ""} onChange={(e) => handleFieldChange("setup_time_minutes", e.target.value)} className="h-9" data-testid="field-setup-time" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Start Time</Label>
                    <Input type="time" value={formData.start_time || ""} onChange={(e) => handleFieldChange("start_time", e.target.value)} className="h-9" data-testid="field-start-time" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Stop Time</Label>
                    <Input type="time" value={formData.stop_time || ""} onChange={(e) => handleFieldChange("stop_time", e.target.value)} className="h-9" data-testid="field-stop-time" />
                  </div>
                </div>
              </div>

              <Separator />

              {/* ── Section 4: Quantities ── */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground">Quantities</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Planned Qty ({selectedProcess.planned_qty_unit})</Label>
                    <Input type="number" step="any" value={formData.planned_qty ?? ""} onChange={(e) => handleFieldChange("planned_qty", e.target.value)} className="h-9" data-testid="field-planned-qty" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Input Qty ({selectedProcess.input_qty_unit})</Label>
                    <Input type="number" step="any" value={formData.input_qty ?? ""} onChange={(e) => handleFieldChange("input_qty", e.target.value)} className="h-9" data-testid="field-input-qty" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Output Qty ({selectedProcess.output_qty_unit})</Label>
                    <Input type="number" step="any" value={formData.output_qty ?? ""} onChange={(e) => handleFieldChange("output_qty", e.target.value)} className="h-9" data-testid="field-output-qty" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Output Item</Label>
                  <Input value={formData.output_item || ""} onChange={(e) => handleFieldChange("output_item", e.target.value)} className="h-9" data-testid="field-output-item" />
                </div>
              </div>

              <Separator />

              {/* ── Section 5: Process-Specific Fields ── */}
              {selectedProcess.specific_fields?.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-foreground">{selectedProcess.process_name} - Specific Inputs</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {selectedProcess.specific_fields.map((f) => (
                      <DynamicField
                        key={f.key}
                        field={f}
                        value={specificData[f.key]}
                        onChange={handleSpecificChange}
                      />
                    ))}
                  </div>
                </div>
              )}

              <Separator />

              {/* ── Section 5b: Custom Fields ── */}
              {selectedProcess.custom_fields?.length > 0 && (
                <>
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                      Custom Fields
                      <Badge variant="secondary" className="text-xs">Custom</Badge>
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      {selectedProcess.custom_fields.map((f) => (
                        <DynamicField
                          key={f.key}
                          field={f}
                          value={customFieldData[f.key]}
                          onChange={handleCustomFieldChange}
                        />
                      ))}
                    </div>
                  </div>
                  <Separator />
                </>
              )}

              {/* ── Section 6: Wastage ── */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground">Wastage</h3>
                <div className="grid grid-cols-2 gap-3">
                  {selectedProcess.wastage_units?.map((unit) => (
                    <div key={unit}>
                      <Label className="text-xs text-muted-foreground mb-1 block">Wastage ({unit})</Label>
                      <Input
                        type="number"
                        step="any"
                        value={wastage[unit] ?? ""}
                        onChange={(e) => handleWastageChange(unit, e.target.value)}
                        className="h-9"
                        data-testid={`field-wastage-${unit.toLowerCase()}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              {/* ── Section 7: Performance & Remarks ── */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground">Performance & Remarks</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Avg Production/Hr ({selectedProcess.avg_production_unit})</Label>
                    <Input type="number" step="any" value={formData.avg_production_per_hr ?? ""} onChange={(e) => handleFieldChange("avg_production_per_hr", e.target.value)} className="h-9" data-testid="field-avg-production" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Remarks</Label>
                  <Textarea value={formData.remarks || ""} onChange={(e) => handleFieldChange("remarks", e.target.value)} rows={2} data-testid="field-remarks" />
                </div>
              </div>
            </>
          )}

          {/* ── Submit ── */}
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={onClose} data-testid="cancel-btn">Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={saving || !selectedOrder || !selectedItemId || !selectedProcessId}
              data-testid="save-log-btn"
            >
              {saving ? "Saving..." : editLog ? "Update Entry" : "Save Entry"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Manage Process Fields Dialog (Super Admin) ────────
function ManageCustomFieldsDialog({ open, onClose, processes, onUpdate }) {
  const [selectedProcId, setSelectedProcId] = useState("");
  const [fields, setFields] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [newLabel, setNewLabel] = useState("");
  const [newType, setNewType] = useState("text");
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [editType, setEditType] = useState("text");

  const selectedProc = processes.find((p) => p.process_id === selectedProcId);

  useEffect(() => {
    if (!open) { setSelectedProcId(""); setFields([]); setCustomFields([]); setEditingField(null); return; }
  }, [open]);

  useEffect(() => {
    if (!selectedProcId) { setFields([]); setCustomFields([]); return; }
    const fetchFields = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/production/processes/${selectedProcId}/fields`);
        setFields(res.data.specific_fields || []);
        setCustomFields(res.data.custom_fields || []);
      } catch { setFields([]); setCustomFields([]); }
      finally { setLoading(false); }
    };
    fetchFields();
  }, [selectedProcId]);

  const handleAddField = async () => {
    if (!newLabel.trim()) return;
    setAdding(true);
    try {
      const res = await axios.post(`${API}/api/production/processes/${selectedProcId}/fields`, {
        label: newLabel.trim(), type: newType
      });
      setFields((prev) => [...prev, res.data]);
      setNewLabel("");
      setNewType("text");
      onUpdate();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to add field");
    } finally { setAdding(false); }
  };

  const handleUpdateField = async (fieldKey) => {
    if (!editLabel.trim()) return;
    try {
      const res = await axios.put(`${API}/api/production/processes/${selectedProcId}/fields/${fieldKey}`, {
        label: editLabel.trim(), type: editType
      });
      setFields((prev) => prev.map((f) => f.key === fieldKey ? res.data : f));
      setEditingField(null);
      setEditLabel("");
      setEditType("text");
      onUpdate();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update field");
    }
  };

  const handleDeleteField = async (fieldKey) => {
    if (!window.confirm("Remove this field? Existing log data for this field will remain but won't display in the form.")) return;
    try {
      await axios.delete(`${API}/api/production/processes/${selectedProcId}/fields/${fieldKey}`);
      setFields((prev) => prev.filter((f) => f.key !== fieldKey));
      onUpdate();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to remove field");
    }
  };

  const handleDeleteCustomField = async (fieldKey) => {
    if (!window.confirm("Remove this custom field?")) return;
    try {
      await axios.delete(`${API}/api/production/processes/${selectedProcId}/custom-fields/${fieldKey}`);
      setCustomFields((prev) => prev.filter((f) => f.key !== fieldKey));
      onUpdate();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to remove field");
    }
  };

  const startEditing = (field) => {
    setEditingField(field.key);
    setEditLabel(field.label);
    setEditType(field.type);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" /> Process Form Field Settings
          </DialogTitle>
          <DialogDescription>
            Configure the input fields that appear in the production log form for each process.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Select Process</Label>
            <Select value={selectedProcId} onValueChange={setSelectedProcId}>
              <SelectTrigger className="h-9" data-testid="cf-process-select">
                <SelectValue placeholder="Choose a process..." />
              </SelectTrigger>
              <SelectContent>
                {processes.map((p) => (
                  <SelectItem key={p.process_id} value={p.process_id}>{p.process_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedProcId && (
            <>
              <Separator />
              {/* Process-Specific Fields */}
              <div className="space-y-2">
                <p className="text-sm font-semibold">
                  Form Fields for <span className="text-primary">{selectedProc?.process_name}</span>
                </p>
                <p className="text-xs text-muted-foreground">
                  These fields appear in the production log entry form for this process. You can add, edit, or delete fields.
                </p>
                {loading ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">Loading...</p>
                ) : fields.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center border rounded-md">No fields defined. Add fields below.</p>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto border rounded-md p-2">
                    {fields.map((f, idx) => (
                      <div key={f.key} className="flex items-center gap-2 p-2 rounded-md bg-muted/30 hover:bg-muted/50 transition-colors" data-testid={`field-row-${f.key}`}>
                        {editingField === f.key ? (
                          <>
                            <Input
                              value={editLabel}
                              onChange={(e) => setEditLabel(e.target.value)}
                              className="h-8 flex-1 text-sm"
                              autoFocus
                              data-testid={`edit-label-${f.key}`}
                            />
                            <Select value={editType} onValueChange={setEditType}>
                              <SelectTrigger className="h-8 w-24" data-testid={`edit-type-${f.key}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="text">Text</SelectItem>
                                <SelectItem value="number">Number</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button size="sm" className="h-8 px-3" onClick={() => handleUpdateField(f.key)} data-testid={`save-field-${f.key}`}>
                              Save
                            </Button>
                            <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setEditingField(null)}>
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <span className="text-xs text-muted-foreground w-6">{idx + 1}.</span>
                            <span className="text-sm font-medium flex-1">{f.label}</span>
                            <Badge variant="outline" className="text-xs">{f.type}</Badge>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEditing(f)} data-testid={`edit-field-${f.key}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeleteField(f.key)} data-testid={`delete-field-${f.key}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Legacy Custom Fields (if any) */}
              {customFields.length > 0 && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <p className="text-sm font-semibold flex items-center gap-2">
                      Additional Custom Fields
                      <Badge variant="secondary" className="text-xs">Legacy</Badge>
                    </p>
                    <div className="space-y-2">
                      {customFields.map((f) => (
                        <div key={f.key} className="flex items-center justify-between p-2 border rounded-md bg-muted/30">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{f.label}</span>
                            <Badge variant="outline" className="text-xs">{f.type}</Badge>
                          </div>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeleteCustomField(f.key)} data-testid={`remove-cf-${f.key}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <Separator />

              {/* Add New Field */}
              <div className="space-y-2">
                <p className="text-sm font-semibold">Add New Field</p>
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Label className="text-xs text-muted-foreground mb-1 block">Field Label</Label>
                    <Input
                      value={newLabel}
                      onChange={(e) => setNewLabel(e.target.value)}
                      placeholder="e.g. Foil Thickness (mm)"
                      className="h-9"
                      onKeyDown={(e) => e.key === "Enter" && handleAddField()}
                      data-testid="cf-new-label"
                    />
                  </div>
                  <div className="w-28">
                    <Label className="text-xs text-muted-foreground mb-1 block">Type</Label>
                    <Select value={newType} onValueChange={setNewType}>
                      <SelectTrigger className="h-9" data-testid="cf-new-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="text">Text</SelectItem>
                        <SelectItem value="number">Number</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleAddField} disabled={adding || !newLabel.trim()} size="sm" className="h-9 px-4" data-testid="cf-add-btn">
                    {adding ? "..." : <><Plus className="h-4 w-4 mr-1" /> Add</>}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── MAIN PAGE ─────────────────────────────────────────
export default function ProductionLogPage() {
  const { user } = useAuth();
  const [processes, setProcesses] = useState([]);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filterProcess, setFilterProcess] = useState("all");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Dialogs
  const [showCreate, setShowCreate] = useState(false);
  const [editLog, setEditLog] = useState(null);
  const [viewLog, setViewLog] = useState(null);
  const [showCustomFields, setShowCustomFields] = useState(false);

  const pageSize = 25;
  const canDelete = ["super_admin", "admin", "production_hod"].includes(user?.role);
  const isSuperAdmin = user?.role === "super_admin";

  const fetchProcesses = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/production/processes`);
      setProcesses(res.data);
    } catch { /* ignore */ }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (filterProcess !== "all") params.process_id = filterProcess;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      if (searchTerm) params.search = searchTerm;
      const res = await axios.get(`${API}/api/production/logs`, { params });
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch {
      setLogs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, filterProcess, filterDateFrom, filterDateTo, searchTerm]);

  useEffect(() => { fetchProcesses(); }, [fetchProcesses]);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.ceil(total / pageSize) || 1;

  const handleDeleteLog = async (logId) => {
    if (!window.confirm("Delete this production log entry?")) return;
    try {
      await axios.delete(`${API}/api/production/logs/${logId}`);
      fetchLogs();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete");
    }
  };

  const processMap = {};
  processes.forEach((p) => { processMap[p.process_id] = p; });

  return (
    <div className="p-6 space-y-6" data-testid="production-log-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl tracking-tight flex items-center gap-2">
            <Factory className="h-6 w-6" /> Production Log
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track input/output for production processes across orders
          </p>
        </div>
        <div className="flex gap-2">
          {isSuperAdmin && (
            <Button variant="outline" onClick={() => setShowCustomFields(true)} data-testid="manage-custom-fields-btn">
              <Settings className="h-4 w-4 mr-2" /> Field Settings
            </Button>
          )}
          <Button onClick={() => setShowCreate(true)} data-testid="new-entry-btn">
            <Plus className="h-4 w-4 mr-2" /> New Entry
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 p-4 border rounded-lg bg-card">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Filter className="h-4 w-4" /> Filters
        </div>
        <div className="w-48">
          <Label className="text-xs text-muted-foreground mb-1 block">Process</Label>
          <Select value={filterProcess} onValueChange={(v) => { setFilterProcess(v); setPage(1); }}>
            <SelectTrigger className="h-9" data-testid="filter-process">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Processes</SelectItem>
              {processes.map((p) => (
                <SelectItem key={p.process_id} value={p.process_id}>{p.process_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground mb-1 block flex items-center gap-1"><CalendarDays className="h-3 w-3" /> From</Label>
          <Input type="date" value={filterDateFrom} onChange={(e) => { setFilterDateFrom(e.target.value); setPage(1); }} className="h-9 w-40" data-testid="filter-date-from" />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground mb-1 block flex items-center gap-1"><CalendarDays className="h-3 w-3" /> To</Label>
          <Input type="date" value={filterDateTo} onChange={(e) => { setFilterDateTo(e.target.value); setPage(1); }} className="h-9 w-40" data-testid="filter-date-to" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs text-muted-foreground mb-1 block">Search</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Order, item, customer, batch, operator..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              className="pl-9 h-9"
              data-testid="filter-search"
            />
          </div>
        </div>
      </div>

      {/* Summary Bar */}
      <div className="flex items-center gap-4 text-sm">
        <span className="text-muted-foreground">{total} entries found</span>
        {filterProcess !== "all" && (
          <Badge variant="secondary">{processMap[filterProcess]?.process_name}</Badge>
        )}
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="w-10">#</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Process</TableHead>
              <TableHead>Order</TableHead>
              <TableHead>Item</TableHead>
              <TableHead>Operator</TableHead>
              <TableHead className="text-right">Input</TableHead>
              <TableHead className="text-right">Output</TableHead>
              <TableHead className="text-right">Efficiency</TableHead>
              <TableHead className="w-28 text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : logs.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">No production log entries found</TableCell></TableRow>
            ) : (
              logs.map((log, idx) => {
                const proc = processMap[log.process_id];
                const efficiency = log.input_qty > 0 ? ((log.output_qty / log.input_qty) * 100).toFixed(1) : "-";
                return (
                  <TableRow key={log.log_id} className="hover:bg-muted/30" data-testid={`log-row-${log.log_id}`}>
                    <TableCell className="text-muted-foreground text-xs">{(page - 1) * pageSize + idx + 1}</TableCell>
                    <TableCell className="text-sm">{formatDate(log.production_date)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs font-normal">{log.process_name}</Badge>
                    </TableCell>
                    <TableCell className="text-sm font-medium">{log.order_number}</TableCell>
                    <TableCell className="text-sm max-w-[150px] truncate">{log.item_name}</TableCell>
                    <TableCell className="text-sm">{log.operator_name || "-"}</TableCell>
                    <TableCell className="text-right text-sm">{log.input_qty ?? "-"} <span className="text-xs text-muted-foreground">{proc?.input_qty_unit}</span></TableCell>
                    <TableCell className="text-right text-sm">{log.output_qty ?? "-"} <span className="text-xs text-muted-foreground">{proc?.output_qty_unit}</span></TableCell>
                    <TableCell className="text-right text-sm">
                      {efficiency !== "-" ? (
                        <span className={Number(efficiency) >= 90 ? "text-green-600" : Number(efficiency) >= 70 ? "text-yellow-600" : "text-red-600"}>
                          {efficiency}%
                        </span>
                      ) : "-"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setViewLog(log)} data-testid={`view-log-${log.log_id}`}>
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditLog(log)} data-testid={`edit-log-${log.log_id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        {canDelete && (
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeleteLog(log.log_id)} data-testid={`delete-log-${log.log_id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} data-testid="prev-page">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} data-testid="next-page">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <LogFormDialog
        open={showCreate || !!editLog}
        onClose={() => { setShowCreate(false); setEditLog(null); }}
        onSave={fetchLogs}
        processes={processes}
        editLog={editLog}
      />

      {/* View Dialog */}
      <ViewLogDialog
        log={viewLog}
        process={viewLog ? processMap[viewLog.process_id] : null}
        open={!!viewLog}
        onClose={() => setViewLog(null)}
      />

      {/* Manage Custom Fields Dialog (Super Admin) */}
      <ManageCustomFieldsDialog
        open={showCustomFields}
        onClose={() => setShowCustomFields(false)}
        processes={processes}
        onUpdate={fetchProcesses}
      />
    </div>
  );
}
